import { z } from 'zod';
import { isoDate, okResponse, phone, trimmed, uuid } from './common.js';

/** Public "register your society" form. Nothing is created until an operator approves it. */
export const signupRequest = z.object({
  societyName: trimmed(3, 120),
  city: trimmed(2, 60),
  address: trimmed(1, 300).optional(),
  adminName: trimmed(2, 80),
  adminPhone: phone,
  adminEmail: z.email().max(120).optional(),
  flatsEstimate: z.number().int().min(1).max(5000).optional(),
  message: trimmed(1, 500).optional(),
  /** Honeypot: real people never see or fill this. Bots that fill every field give themselves away. */
  website: z.string().max(200).optional(),
});
export type SignupRequest = z.infer<typeof signupRequest>;

export const signupResponse = okResponse;

export const signupStatus = z.enum(['pending', 'approved', 'rejected']);

export const signupDto = z.object({
  id: uuid,
  societyName: z.string(),
  city: z.string(),
  address: z.string().nullable(),
  adminName: z.string(),
  adminPhone: z.string(),
  adminEmail: z.string().nullable(),
  flatsEstimate: z.number().nullable(),
  message: z.string().nullable(),
  status: signupStatus,
  communityId: uuid.nullable(),
  decisionNote: z.string().nullable(),
  createdAt: isoDate,
  decidedAt: isoDate.nullable(),
});
export type SignupDto = z.infer<typeof signupDto>;

export const signupListQuery = z.object({ status: signupStatus.optional() });
export const signupList = z.array(signupDto);

/** The one-time code is shown to the operator once, to be sent to the new admin. */
export const approveResponse = z.object({
  communityId: uuid,
  slug: z.string(),
  adminName: z.string(),
  adminPhone: z.string(),
  code: z.string(),
  codeExpiresAt: isoDate,
});
export type ApproveResponse = z.infer<typeof approveResponse>;

export const rejectSignup = z.object({ note: trimmed(1, 300).optional() });
